import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CssBaseline, Link, Paper, TextField, ThemeProvider, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { CUSTOMER_DASHBOARD } from '../../../services/constants/URIConstants';
import {
  currentCustomerSession,
  portalErrorMessage,
  saveCustomerSession,
  sendLoginCode,
  verifyLoginCode,
} from '../../../services/request/CustomerPortal';
import { isEmail } from '../../../utils/customerPortalFormat';
import { brand, portalTheme } from '../portal/portalTheme';
import { BrandMark, WealthGraphic } from '../portal/PortalUi';
import { LocalTestingBanner } from '../portal/CustomerPortalLayout';
import { ReleasepilotAffiliation } from '../../../shared-components/branding/ReleasepilotAffiliation';
import { LanguageSwitcher, TranslationKey, useTranslation } from '../../../i18n';

type Message = { key: TranslationKey; vars?: Record<string, string> } | { text: string };

const CustomerLogin: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Message | null>(null);
  const [info, setInfo] = useState<Message | null>(params.get('expired') ? { key: 'portal.login.expired' } : null);
  const show = (message: Message) => ('text' in message ? message.text : t(message.key, message.vars));

  useEffect(() => {
    if (currentCustomerSession()) navigate(CUSTOMER_DASHBOARD, { replace: true });
  }, [navigate]);

  const sendCode = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!isEmail(email)) {
      setError({ key: 'portal.login.invalidEmail' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await sendLoginCode(email);
      setStep('otp');
      setInfo({ key: 'portal.login.codeSent', vars: { email: email.trim() } });
    } catch (err) {
      setError({ text: portalErrorMessage(err, t('portal.login.sendFailed')) });
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      setError({ key: 'portal.login.invalidCode' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await verifyLoginCode(email, otp);
      saveCustomerSession(result.token, result.customerName);
      navigate(CUSTOMER_DASHBOARD, { replace: true });
    } catch (err) {
      setError({ text: portalErrorMessage(err, t('portal.login.verifyFailed')) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ThemeProvider theme={portalTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: brand.ivory }}>
        <LocalTestingBanner />
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <Box
            sx={{
              bgcolor: brand.navy,
              color: brand.ivory,
              p: { xs: 4, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              justifyContent: 'center',
              gap: 2,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <BrandMark size={88} />
            <Typography variant="overline" sx={{ color: brand.gold }}>
              {t('portal.layout.brand')}
            </Typography>
            <Typography variant="h3" component="h1">
              {t('portal.layout.product')}
            </Typography>
            <Typography sx={{ color: brand.mist, maxWidth: 380 }}>{t('portal.login.tagline')}</Typography>
            <Box sx={{ mt: 2 }}>
              <WealthGraphic size={240} />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: { xs: 3, md: 6 },
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', justifyContent: 'flex-end' }}>
              <LanguageSwitcher tone="light" />
            </Box>
            <Paper sx={{ p: { xs: 3, md: 5 }, width: '100%', maxWidth: 440 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {t('portal.login.title')}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {t('portal.login.intro')}
              </Typography>
              {info ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {show(info)}
                </Alert>
              ) : null}
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {show(error)}
                </Alert>
              ) : null}

              {step === 'email' ? (
                <Box component="form" onSubmit={sendCode} sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    label={t('portal.login.email')}
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={busy}>
                    {busy ? t('common.sending') : t('portal.login.sendCode')}
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={verify} sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    label={t('portal.login.code')}
                    autoComplete="one-time-code"
                    autoFocus
                    value={otp}
                    onChange={event => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={busy}>
                    {busy ? t('portal.login.checking') : t('portal.login.verify')}
                  </Button>
                  <Button onClick={() => sendCode()} disabled={busy}>
                    {t('portal.login.resend')}
                  </Button>
                  <Button
                    onClick={() => {
                      setStep('email');
                      setOtp('');
                      setInfo(null);
                      setError(null);
                    }}
                  >
                    {t('portal.login.differentEmail')}
                  </Button>
                </Box>
              )}
              <Link component={RouterLink} to="/customer/guide" sx={{ display: 'block', mt: 3, textAlign: 'center', color: brand.warning }}>
                {t('portal.login.guideLink')}
              </Link>
            </Paper>
          </Box>
        </Box>
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <ReleasepilotAffiliation compact />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default CustomerLogin;
